import { Funnel, MagnifyingGlass, XCircle } from '@phosphor-icons/react';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { Badge, BadgeVariant } from 'src/components/Badge';
import { Button } from 'src/components/Button';
import { CallDetailsModal } from 'src/components/calls/CallDetailsModal';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Modal } from 'src/components/Modal';
import {
  SideDropAction,
  SideDropActions,
} from 'src/components/SideDropActions';
import Table, { Header, Row } from 'src/components/Table';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import {
  ActivityList,
  ActivityItemData,
} from 'src/pages/Automations/components/ui/ActivityList';
import { ScheduledCallsFiltersModal } from 'src/components/ScheduledCallsFiltersModal';
import { Agent } from 'src/interfaces/agent.interface';
import {
  Call,
  ScheduledCall,
  ScheduledCallFilters,
} from 'src/interfaces/call.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import {
  WorkflowExecutionCancelationReason,
  WorkflowExecutionThreadStatus,
} from 'src/interfaces/workflows.interface';
import { friendlyDateFormatter } from 'src/utils/date';
import { processPhoneNumber } from 'src/utils/number';

const CONTACT_REACHED_CANCELATION_REASONS =
  new Set<WorkflowExecutionCancelationReason>([
    WorkflowExecutionCancelationReason.REASON_CALL_ANSWERED,
    WorkflowExecutionCancelationReason.REASON_INCOMING_CALL,
  ]);

const STATUS_BADGE_VARIANTS: Record<
  WorkflowExecutionThreadStatus,
  BadgeVariant
> = {
  [WorkflowExecutionThreadStatus.CANCELED]: 'secondary',
  [WorkflowExecutionThreadStatus.COMPLETED]: 'success',
  [WorkflowExecutionThreadStatus.ERRORED]: 'error',
  [WorkflowExecutionThreadStatus.PAUSED]: 'secondary',
  [WorkflowExecutionThreadStatus.RUNNING]: 'secondary',
  [WorkflowExecutionThreadStatus.RATE_LIMITED]: 'secondary',
};

const CANCELABLE_STATUSES = new Set<WorkflowExecutionThreadStatus>([
  WorkflowExecutionThreadStatus.PAUSED,
  WorkflowExecutionThreadStatus.RUNNING,
  WorkflowExecutionThreadStatus.RATE_LIMITED,
]);

const getStatusDisplay = (scheduledCall: ScheduledCall) => {
  const { status, cancelation_reason } = scheduledCall;

  if (status === WorkflowExecutionThreadStatus.CANCELED) {
    if (
      cancelation_reason &&
      CONTACT_REACHED_CANCELATION_REASONS.has(cancelation_reason)
    ) {
      return 'Canceled (Contact Reached)';
    }
    return 'Canceled';
  }

  if (status === WorkflowExecutionThreadStatus.ERRORED) {
    return 'Errored';
  }

  if (status === WorkflowExecutionThreadStatus.PAUSED) {
    return 'Inactive';
  }

  if (status === WorkflowExecutionThreadStatus.RATE_LIMITED) {
    return 'Queued (Max Concurrent Calls)';
  }

  if (status === WorkflowExecutionThreadStatus.RUNNING) {
    return 'Scheduled';
  }

  return 'Completed';
};

const headers: Header[] = [
  { key: 'id', label: 'ID', width: '8rem', disableSorting: true },
  { key: 'type', label: 'Type', width: '8rem', disableSorting: true },
  { key: 'created_at', label: 'Created At', width: '12rem' },
  { key: 'agent', label: 'Agent', width: '12rem', disableSorting: true },
  {
    key: 'from_number',
    label: 'From',
    width: '10rem',
    disableSorting: true,
  },
  {
    key: 'to_number',
    label: 'To',
    width: '10rem',
    disableSorting: true,
  },
  {
    key: 'resume_at',
    label: 'Scheduled Time',
    width: '12rem',
    disableSorting: true,
  },
  {
    key: 'status',
    label: 'Status',
    width: '10rem',
    disableSorting: true,
  },
  {
    key: 'activity',
    label: 'Activity',
    width: '18rem',
    disableSorting: true,
  },
  {
    key: 'actions',
    label: 'Actions',
    width: '6rem',
    disableSorting: true,
    align: 'center',
  },
];

const getDefaultCreatedAtFilter = () => {
  const end = endOfDay(new Date());
  const start = startOfDay(subDays(end, 27));

  return {
    comparison: 'btwn',
    value: [start.toISOString(), end.toISOString()] as [string, string],
  };
};

const createDefaultScheduledCallFilters = (): ScheduledCallFilters => ({
  created_at: getDefaultCreatedAtFilter(),
});

export const ScheduledCalls = () => {
  const { get, post } = useAuthedRequest();
  const notification = useNotification();

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });

  const [filters, setFilters] = useState<ScheduledCallFilters>(
    createDefaultScheduledCallFilters,
  );

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [isCallDetailsModalOpen, setIsCallDetailsModalOpen] = useState(false);

  const [selectedScheduledCall, setSelectedScheduledCall] =
    useState<ScheduledCall | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);

  const [agents, setAgents] = useState<Agent[]>([]);

  const openCallDetails = useCallback(
    async (callId: string) => {
      try {
        const call = await get(`/calls/${callId}`);
        setSelectedCall(call);
        setIsCallDetailsModalOpen(true);
      } catch (error) {
        console.error('Failed to load call details', error);
        notification.error('Failed to load call details');
      }
    },
    [get, notification],
  );

  const renderActivity = useCallback(
    (scheduledCall: ScheduledCall) => {
      const items: ActivityItemData[] = [];

      if (scheduledCall.linked_call_id) {
        const { linked_call_id } = scheduledCall;
        items.push({
          completedAt: scheduledCall.created_at,
          label: 'Initial Call',
          icon: MagnifyingGlass,
          onClick: () => openCallDetails(linked_call_id),
        });
      }

      if (scheduledCall.call_id) {
        const { call_id } = scheduledCall;
        items.push({
          completedAt: scheduledCall.resume_at || '',
          label: 'Callback',
          icon: MagnifyingGlass,
          onClick: () => openCallDetails(call_id),
        });
      }

      if (items.length === 0) {
        return '—';
      }

      return <ActivityList items={[...items].reverse()} />;
    },
    [openCallDetails],
  );

  const openCancelModal = useCallback((scheduledCall: ScheduledCall) => {
    setSelectedScheduledCall(scheduledCall);
    setIsCancelModalOpen(true);
  }, []);

  const renderActions = useCallback(
    (scheduledCall: ScheduledCall) => {
      const isCancelable = CANCELABLE_STATUSES.has(scheduledCall.status);

      const actions: SideDropAction[] = [
        {
          label: 'Cancel',
          Icon: XCircle,
          onClick: () => openCancelModal(scheduledCall),
          disabled: !isCancelable,
        },
      ];

      return (
        <div
          className="flex justify-center items-center"
          onClick={(event) => event.stopPropagation()}
        >
          <SideDropActions options={actions} />
        </div>
      );
    },
    [openCancelModal],
  );

  const mapScheduledCallsToRows = useCallback(
    (scheduledCalls: ScheduledCall[]): Row[] => {
      return scheduledCalls.map((scheduledCall) => ({
        id: (
          <CopyableField value={scheduledCall.id} notification={notification} />
        ),
        agent: (
          <div className="flex flex-col">
            <span>{scheduledCall.agent?.name || 'Unknown'}</span>
            {scheduledCall.agent?.id ? (
              <CopyableField
                value={scheduledCall.agent.id}
                notification={notification}
                className="mt-1 text-neutral-400"
              />
            ) : (
              <span className="text-neutral-400">-</span>
            )}
          </div>
        ),
        created_at: friendlyDateFormatter(new Date(scheduledCall.created_at)),
        type: (
          <Badge variant="primary" className="whitespace-nowrap">
            Callback
          </Badge>
        ),
        from_number: processPhoneNumber(scheduledCall.from_number),
        to_number: processPhoneNumber(scheduledCall.to_number),
        resume_at: scheduledCall.resume_at
          ? friendlyDateFormatter(new Date(scheduledCall.resume_at))
          : '—',
        status: (
          <Badge variant={STATUS_BADGE_VARIANTS[scheduledCall.status]}>
            {getStatusDisplay(scheduledCall)}
          </Badge>
        ),
        activity: renderActivity(scheduledCall),
        actions: renderActions(scheduledCall),
      }));
    },
    [notification, renderActivity, renderActions],
  );

  const fetchScheduledCalls = useCallback(
    async (page: number, size: number) => {
      setLoading(true);
      try {
        const formattedFilters: ScheduledCallFilters = {};

        if (filters.agent) {
          formattedFilters.agent = filters.agent;
        }
        if (filters.contact_number) {
          formattedFilters.contact_number = filters.contact_number;
        }
        if (filters.from_number) {
          formattedFilters.from_number = filters.from_number;
        }
        if (filters.to_number) {
          formattedFilters.to_number = filters.to_number;
        }
        if (filters.statuses && filters.statuses.length > 0) {
          formattedFilters.statuses = filters.statuses;
        }
        if (filters.created_at) {
          formattedFilters.created_at = filters.created_at;
        }

        const queryObject: QueryObject = {
          page: String(page),
          size: String(size),
          filters: JSON.stringify(formattedFilters),
        };

        if (sortConfig.key) {
          queryObject.sort_column = sortConfig.key;
          queryObject.sort_desc =
            sortConfig.direction === 'desc' ? 'true' : 'false';
        }

        const query = new URLSearchParams(
          queryObject as Record<string, string>,
        );

        const response = await get(`/calls/scheduled?${query.toString()}`);

        setTotalItems(response.total);
        setRows(mapScheduledCallsToRows(response.items));
      } catch (error) {
        console.error('Failed to fetch scheduled calls', error);
        notification.error('Failed to fetch scheduled calls');
      } finally {
        setLoading(false);
      }
    },
    [filters, get, notification, sortConfig, mapScheduledCallsToRows],
  );

  useEffect(() => {
    fetchScheduledCalls(currentPage, pageSize);
  }, [currentPage, pageSize, fetchScheduledCalls]);

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const handleCancelScheduledCall = async () => {
    if (!selectedScheduledCall) return;

    try {
      setIsCancelLoading(true);
      await post(
        `/workflow_executions/${selectedScheduledCall.execution_id}/cancel`,
        {},
      );
      notification.success('Scheduled call canceled');
      setIsCancelModalOpen(false);
      setSelectedScheduledCall(null);
      fetchScheduledCalls(currentPage, pageSize);
    } catch (error) {
      console.error('Failed to cancel scheduled call', error);
      notification.error('Failed to cancel scheduled call');
    } finally {
      setIsCancelLoading(false);
    }
  };

  const handleCloseCallDetails = () => {
    setSelectedCall(null);
    setIsCallDetailsModalOpen(false);
  };

  const searchAgents = useCallback(
    async (name: string) => {
      const queryObject: QueryObject = {
        ...(name.length !== 0 && { filters: JSON.stringify({ name }) }),
      };
      const query = new URLSearchParams(queryObject as Record<string, string>);

      const data = await get(`/agents?${query.toString()}`);
      setAgents(data.items);

      return data.items.map((agent: Agent) => ({
        value: agent.id,
        label: agent.name,
      }));
    },
    [get],
  );

  const handleApplyFilters = (newFilters: ScheduledCallFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  return (
    <div className="flex-1">
      <Heading
        title="Scheduled Calls"
        subtitle="View and manage upcoming callback calls"
      />

      <Can operation="read" subject={Subject.CALLS}>
        <ContentWrapper>
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <Button
                className="border-none flex items-center justify-center"
                color="primary"
                onClick={() => setIsFilterModalOpen(true)}
              >
                <Funnel className="mr-2" size={18} />
                Filters
              </Button>

              <div className="flex items-center gap-3" />
            </div>
          </div>

          <Table
            headers={headers}
            rows={rows}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={pageSize}
          />
        </ContentWrapper>
      </Can>

      {selectedCall && (
        <CallDetailsModal
          call={selectedCall}
          isOpen={isCallDetailsModalOpen}
          onClose={handleCloseCallDetails}
        />
      )}

      <Modal
        title="Cancel Scheduled Call"
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        actionButton={
          <Button
            onClick={handleCancelScheduledCall}
            disabled={isCancelLoading}
          >
            Cancel Scheduled Call
          </Button>
        }
        className="w-auto max-w-md"
      >
        <div className="text-center">
          Are you sure you want to cancel this scheduled call?
        </div>

        <div className="my-4 border border-neutral-200 rounded-md text-left">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-neutral-200">
                <td className="px-4 py-2 font-medium text-neutral-500">
                  Agent
                </td>
                <td className="px-4 py-2">
                  {selectedScheduledCall?.agent?.name || 'Unknown'}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 font-medium text-neutral-500">
                  To Number
                </td>
                <td className="px-4 py-2">
                  {processPhoneNumber(selectedScheduledCall?.to_number || '')}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal>

      <ScheduledCallsFiltersModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onApplyFilters={handleApplyFilters}
        agents={agents}
        searchAgents={searchAgents}
        loading={loading}
      />
    </div>
  );
};
